/**
 * /industrie/iso-50001 — Page éditoriale bonus
 *
 * Présente la norme ISO 50001 (Système de Management de l'Énergie),
 * son articulation avec l'audit énergétique réglementaire NF EN 16247,
 * et son rôle dans une démarche industrielle de pilotage des IPE.
 *
 * Sources réglementaires :
 *  - ISO 50001:2018 — Systèmes de management de l'énergie
 *  - Code de l'énergie, article R. 233-2 (dispense audit si ISO 50001
 *    couvre ≥ 80 % du périmètre énergétique de l'entreprise)
 *  - Décret n° 2014-1393 du 24 nov. 2014 — audit obligatoire
 *  - Directive 2012/27/UE — efficacité énergétique
 */

import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Container } from '../../../components/layout/Container';
import { Button } from '../../../components/ui/button';
import { BreadcrumbListLd } from '../../../components/seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../../../components/seo/jsonld/FAQPage';
import { getIcon } from '../../../lib/lucide-icons';
import { cn } from '../../../lib/cn';
import { getStubTokens, SITE_URL } from '../../../components/audience/_shared/tone-tokens';

const t = getStubTokens('industrie');

const KEY_POINTS = [
  {
    iconName: 'shield-check',
    label: 'Norme internationale',
    description:
      "ISO 50001:2018 est la norme internationale de référence pour les systèmes de management de l'énergie (SMÉ). Applicable à toute organisation, indépendamment de sa taille ou de son secteur.",
  },
  {
    iconName: 'gauge',
    label: 'IPE & revue énergétique',
    description:
      "La démarche structure : politique énergétique, revue énergétique initiale, définition d'Indicateurs de Performance Énergétique (IPE), plan d'action, mesure, amélioration continue (cycle PDCA).",
  },
  {
    iconName: 'file-search',
    label: 'Dispense audit réglementaire',
    description:
      "Une certification ISO 50001 couvrant au moins 80 % des consommations énergétiques de l'entreprise vaut dispense de l'audit énergétique obligatoire (article R. 233-2 du Code de l'énergie).",
  },
  {
    iconName: 'cog',
    label: 'Amélioration continue',
    description:
      "Contrairement à un audit ponctuel, ISO 50001 est un système vivant : revues périodiques, audits internes, audits de certification tous les 3 ans (avec audits de surveillance annuels).",
  },
];

const STEPS = [
  {
    title: 'Engagement de la direction & politique énergétique',
    description:
      "Définition d'une politique énergétique formalisée, désignation d'un responsable énergie (RM), allocation des moyens. La direction porte la démarche pour qu'elle vive dans la durée.",
  },
  {
    title: "Revue énergétique initiale",
    description:
      "État des lieux complet : analyse des consommations sur 24 à 36 mois, identification des usages énergétiques significatifs (USE), définition de la situation énergétique de référence (SER).",
  },
  {
    title: 'Définition des IPE & objectifs',
    description:
      "Choix d'Indicateurs de Performance Énergétique cohérents (kWh/tonne produite, kWh/m² chauffé, ratios spécifiques au process), fixation d'objectifs chiffrés sur 1 et 3 ans.",
  },
  {
    title: "Plan d'actions priorisé",
    description:
      "Hiérarchisation des opérations d'efficacité énergétique par TRI ou par contribution aux objectifs IPE. Articulation avec le financement CEE IND, Fonds chaleur et Fonds décarbonation.",
  },
  {
    title: 'Mise en œuvre & mesure',
    description:
      "Déploiement des actions, instrumentation des USE (sous-comptages), suivi mensuel des IPE, ajustement du plan en fonction des écarts. Outils de monitoring énergétique (logiciels ou Excel structurés).",
  },
  {
    title: 'Audits & certification',
    description:
      "Audits internes périodiques, revue de direction annuelle, audit de certification par un organisme accrédité (AFNOR, Bureau Veritas, etc.). Cycle 3 ans avec audits de surveillance intermédiaires.",
  },
];

const FAQ = [
  {
    question: "Quelle différence entre audit énergétique NF EN 16247 et certification ISO 50001 ?",
    answer:
      "L'audit NF EN 16247 est une photographie ponctuelle réalisée tous les 4 ans pour les grandes entreprises (obligation réglementaire). ISO 50001 est un système de management vivant : politique, IPE, plan d'action, revue de direction, amélioration continue. Une certification ISO 50001 couvrant ≥ 80 % du périmètre énergétique de l'entreprise dispense de l'audit réglementaire (article R. 233-2 du Code de l'énergie).",
  },
  {
    question: "Combien de temps faut-il pour obtenir la certification ISO 50001 ?",
    answer:
      "Variable selon la maturité énergétique du site et la taille de l'entreprise : généralement 12 à 24 mois entre la décision d'engagement et la certification, en incluant la revue énergétique initiale, la définition des IPE, la mise en œuvre des premières actions, les audits internes et l'audit de certification par un organisme accrédité.",
  },
  {
    question: "Quel est le coût d'une démarche ISO 50001 ?",
    answer:
      "Très variable selon la taille du périmètre, le nombre de sites, le niveau d'accompagnement externe nécessaire et les coûts d'instrumentation (sous-comptages). Il convient d'arbitrer entre coût de la démarche et bénéfices attendus : économies pilotées par les IPE, dispense de l'audit obligatoire, image RSE, accès facilité à certains financements (Fonds décarbonation, prêts Bpifrance climat).",
  },
  {
    question: "Doit-on certifier tous les sites du groupe ?",
    answer:
      "Non. Le périmètre certifié peut être adapté : un seul site, plusieurs sites, ou un périmètre multi-pays. Pour bénéficier de la dispense d'audit réglementaire, il faut couvrir au moins 80 % des consommations énergétiques de l'entreprise au sens du Code de l'énergie. Une démarche progressive site par site est fréquente.",
  },
  {
    question: "ISO 50001 facilite-t-elle l'accès aux aides ?",
    answer:
      "Indirectement, oui. Les IPE et la traçabilité énergétique imposés par ISO 50001 facilitent le montage des dossiers CEE IND (justificatifs robustes), du Fonds chaleur ADEME (suivi performance) et du Fonds décarbonation industrie (rapport ESG / trajectoire chiffrée). Certains appels à projets ADEME valorisent même la certification dans la grille de notation.",
  },
];

export default function Iso50001Page() {
  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Industrie', url: `${SITE_URL}/industrie` },
          { name: 'ISO 50001', url: `${SITE_URL}/industrie/iso-50001` },
        ]}
      />
      <FAQPageLd items={FAQ.map((f) => ({ question: f.question, answer: f.answer }))} />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-stone-50 to-paper pt-12 pb-12 md:pt-16 md:pb-16">
        <div aria-hidden className={cn('pointer-events-none absolute inset-0', t.heroHalo)} />
        <Container size="narrow">
          <nav aria-label="Fil d'Ariane" className="mb-6 text-[12px] text-stone-500">
            <a href="/industrie" className={cn('transition-colors', t.linkColor)}>
              Industrie
            </a>
            <span className="mx-1.5 text-stone-300">/</span>
            <span className="font-medium text-stone-700">ISO 50001</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-10">
            <div className="min-w-0 flex-1">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]',
                    t.eyebrowChip,
                  )}
                >
                  Système de management de l’énergie
                </span>
              </div>

              <h1 className="h-hero-lg">
                ISO 50001
              </h1>

              <p className="mt-4 text-lg text-stone-600 md:text-xl">
                ISO 50001:2018 est la norme internationale qui structure les systèmes de management
                de l&rsquo;énergie. Au-delà d&rsquo;une certification, c&rsquo;est un cadre de
                pilotage qui transforme votre démarche énergétique en routine industrielle :
                politique, IPE, plan d&rsquo;action, mesure, amélioration continue. Pour les
                grandes entreprises, elle peut dispenser de l&rsquo;audit énergétique réglementaire
                NF EN 16247-1.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button asChild size="lg" variant={t.buttonVariant} className={cn('group', t.ctaShadow)}>
                  <a href="/contact?source=industrie&besoin=iso-50001">
                    Demander un cadrage ISO 50001
                    <ArrowRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="ghost">
                  <a href="/industrie/operations/audit-reglementaire">
                    Voir l&rsquo;audit NF EN 16247
                  </a>
                </Button>
              </div>
            </div>

            <aside className="w-full shrink-0 md:w-[260px]">
              <div
                className={cn(
                  'rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)] ring-1 ring-inset',
                  t.sideCardRing,
                )}
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">
                  Cycle de certification
                </p>
                <p className={cn('mt-2 text-3xl font-black tracking-tight', t.gradientText)}>
                  3 ans
                </p>
                <p className="mt-2 text-[12px] text-stone-500">
                  Audit initial + 2 audits de surveillance annuels + renouvellement
                </p>
              </div>
            </aside>
          </div>
        </Container>
      </section>

      {/* ── Points clés ── */}
      <section className="py-14 md:py-20">
        <Container size="narrow">
          <div className="mb-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
              L&apos;essentiel
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-ink md:text-3xl">
              Ce qu&apos;ISO 50001 change concrètement
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {KEY_POINTS.map((point) => {
              const Icon = getIcon(point.iconName);
              return (
                <div
                  key={point.label}
                  className={cn(
                    'rounded-2xl border border-stone-200 bg-paper p-5',
                    'shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-150 ease-out',
                    'hover:border-stone-300 hover:shadow-[0_4px_16px_-4px_rgba(10,10,10,.06)]',
                  )}
                >
                  <span
                    className={cn(
                      'mb-3 inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
                      t.iconChipBg,
                      t.iconChipText,
                      t.iconChipRing,
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  <p className="text-[14px] font-semibold text-ink">{point.label}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-stone-600">
                    {point.description}
                  </p>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── Vertical stepper (déroulement) ── */}
      <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
        <Container size="narrow">
          <div className="mb-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
              Déroulement
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-ink md:text-3xl">
              Les 6 étapes d&apos;une démarche ISO 50001
            </h2>
            <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-stone-600">
              Cadre normatif ISO 50001:2018 (cycle PDCA — Plan, Do, Check, Act). Le calendrier
              effectif dépend de la taille du périmètre et du niveau d&apos;instrumentation
              initial du site.
            </p>
          </div>

          <ol className="space-y-3">
            {STEPS.map((step, idx) => (
              <li
                key={step.title}
                className="flex items-start gap-4 rounded-2xl border border-stone-200 bg-paper p-5"
              >
                <span
                  className={cn(
                    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[14px] font-bold tabular-nums ring-1 ring-inset',
                    t.iconChipBg,
                    t.iconChipText,
                    t.iconChipRing,
                  )}
                >
                  {idx + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-ink">{step.title}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-stone-600">
                    {step.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      {/* ── FAQ ── */}
      <section className="py-14 md:py-20">
        <Container size="narrow">
          <div className="mb-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
              FAQ ISO 50001
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-ink md:text-3xl">
              Vos questions sur ISO 50001
            </h2>
          </div>
          <div className="divide-y divide-stone-100 overflow-hidden rounded-2xl border border-stone-200 bg-paper">
            {FAQ.map((item) => (
              <details
                key={item.question}
                className="group p-5 transition-colors duration-150 ease-out hover:bg-stone-50/60"
              >
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-[15px] font-semibold text-ink marker:hidden">
                  <span>{item.question}</span>
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-stone-100 text-stone-500 transition-transform duration-150 ease-out group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-[14px] leading-relaxed text-stone-600">{item.answer}</p>
              </details>
            ))}
          </div>
        </Container>
      </section>

      {/* ── CTA final ── */}
      <section className="pb-20">
        <Container size="narrow">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-ink via-stone-900 to-ink p-8 text-center md:p-12">
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute -inset-x-12 -bottom-16 h-32 rounded-full blur-3xl',
                t.ctaFinalAccentHalo,
              )}
            />
            <div className="relative">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
                Démarche personnalisée
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold text-ivory-50 md:text-3xl">
                Cadrer votre démarche ISO 50001
              </h2>
              <p className="mt-3 text-stone-400">
                Notre BE thermique partenaire vous accompagne sur la revue énergétique initiale,
                la définition des IPE et le plan d&apos;actions priorisé. Articulation avec les
                CEE IND, le Fonds chaleur ADEME et le Fonds décarbonation industrie.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button asChild size="lg" variant={t.buttonVariant}>
                  <a href="/contact?source=industrie&besoin=iso-50001">
                    Demander un cadrage
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="/industrie/diagnostic-cee">Lancer un diagnostic CEE</a>
                </Button>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-[12px] text-stone-500">
                {['Accord de confidentialité disponible', 'BE thermique partenaire', 'Compatible CEE IND'].map((b) => (
                  <span key={b} className="inline-flex items-center gap-1">
                    <CheckCircle2 className={cn('h-3 w-3', t.checkColor)} />
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
